import {first, last, initial, drop, take} from 'lodash';
import {
  Expressions,
  Component,
  matchExpression,
  Plugins,
  type ExpressionPath,
} from '@packages/systems/wfdl';
import {weakMemo} from '@packages/utilities/memo';
import {
  EElement,
  type ExpressionElement,
  type Expression,
} from '@packages/systems/wfdl-ast';
import {
  getGuidNullable,
  type NativeId,
  getInstanceSuffix,
  toNativeIdPath,
  convertElementIdToNativeId,
  getInstanceIndicesFromNativeId,
  generateInstanceNativeId,
} from '@packages/systems/core/utils/NativeIdUtils';
import {
  emptyArray,
  constantTrue,
  constantFalse,
} from '@packages/utilities/fp/utils';
import {
  getComponentInstRootElement,
  getComponentElementById,
  getComponentRootElement,
  isUserComponentRootId,
  isUserComponentInst,
  getPageComponentRender,
  getPageComponentRootElement,
  pageComponentType,
  getCurrentComponent,
  getCurrentComponentType,
  type MissedFocusSegmentResolver,
  findFurthestAncestor,
} from '@packages/utilities/core-elements';
// Direct-file import to skip the @packages/systems/component-slots barrel,
// which triggers user-components/properties initialization and re-enters
// platform mid-load through DynamoElementUtils/isInside.
// eslint-disable-next-line webflow/module-top-level-imports
import {
  findSlotDefaultElement,
  getSlotDefaultComponentInstances,
  resolveSlotDefaultPathContext,
  type SlotDefaultComponentBoundary,
} from '@packages/systems/component-slots/SlotDefaultUtils';
import {
  isProjectingElement,
  isRichText,
} from '@packages/utilities/elementTypeUtils';
import {namesEqual, getName} from '@packages/systems/wfdl-Qualified';

type SymbolInstanceVisitor = (
  symbolInstance: ExpressionElement,
  symbolRoot: ExpressionElement
) => void;

/**
 * Given a native id path, a root element, and a component map: will call the `visit` function with
 * the symbol instances and their root element along the path.
 */
export const visitNativeIdPathSymbolInstances = (
  nativeIdPath: NativeId[],
  initialRootElem: ExpressionElement,
  componentMap: Component.ComponentMap,
  visit: SymbolInstanceVisitor,
  plugins?: Plugins.PluginsType
) => {
  let currentRoot: ExpressionElement | null = initialRootElem;
  let currentOwnerType: ExpressionElement['type'] | null = null;

  initial(nativeIdPath).forEach((nativeId) => {
    const symbolInstanceId = getGuidNullable(nativeId);

    let symbolInstance = null;
    if (symbolInstanceId) {
      // If we have a current root, we try to find the symbol instance in its render
      const fromCurrentRoot = currentRoot
        ? Expressions.getElementById(EElement(currentRoot), symbolInstanceId)
        : null;

      // if we don't find it, we try to find it in the initial root.
      // check if it's a symbol instance in a slot
      const fromInitialRoot = Expressions.getElementById(
        EElement(initialRootElem),
        symbolInstanceId
      );

      const ownerComponent =
        !fromCurrentRoot && !fromInitialRoot && currentOwnerType
          ? componentMap.get(currentOwnerType)
          : undefined;
      const authoredInstance = ownerComponent
        ? getComponentElementById(ownerComponent, symbolInstanceId)
        : null;

      // if we don't find anything, we try to find it in the slot defaults
      const canUseDefaults =
        !fromCurrentRoot &&
        !fromInitialRoot &&
        !authoredInstance &&
        currentOwnerType &&
        plugins;
      const fromDefaults = canUseDefaults
        ? (findSlotDefaultElement(
            plugins,
            componentMap,
            currentOwnerType!,
            symbolInstanceId
          )?.element ?? null)
        : null;

      symbolInstance =
        fromCurrentRoot || fromInitialRoot || authoredInstance || fromDefaults;
    }

    const maybeRootElement =
      symbolInstance &&
      getComponentInstRootElement(componentMap)(symbolInstance);

    if (maybeRootElement && symbolInstance) {
      visit(symbolInstance, maybeRootElement);
      currentRoot = maybeRootElement;
      currentOwnerType = symbolInstance.type;
    } else {
      // If we are here, an error occured while we were visiting the native id path.
      // By setting `currentRoot` to `null` we ensure we don't call the `visit` callback anymore.
      currentRoot = null;
      // A stale owner type would let a later segment resolve against the wrong
      // component's defaults, so fail cleanly instead.
      currentOwnerType = null;
    }
  });
};

type SymbolInstanceToRootIdMap = {
  [key: string]: string;
};

/**
 * Given a native id path and a component map, creates a map of symbol instances to their
 * corresponding symbol roots in the component map.
 *
 * Input path: ['sym-1', 'sym-2', 'some-element']
 * Output map:
 * {
 *  'sym-1': 'root-1',
 *  'sym-2': 'root-2',
 * }
 */
const createSymbolInstanceIdToRootIdMap = (
  nativeIdPath: NativeId[],
  componentMap: Component.ComponentMap
): SymbolInstanceToRootIdMap | null => {
  const instanceIdToRootIdMap: SymbolInstanceToRootIdMap = {};
  const rootElem = getPageComponentRootElement(componentMap);
  if (!rootElem) return null;

  // Visit symbol instances along the path and populate the map.
  visitNativeIdPathSymbolInstances(
    nativeIdPath,
    rootElem,
    componentMap,
    (symbolInstance, instanceRootElement) => {
      const instanceId = Expressions.getElementId(symbolInstance);
      const rootElementId = Expressions.getElementId(instanceRootElement);
      instanceIdToRootIdMap[instanceId] = rootElementId;
    }
  );

  return instanceIdToRootIdMap;
};

/**
 * Given a native id path and a mapping of symbol instance ids to their corresponding root ids
 * in a component map, will construct all paths leading to the root elements of instances within the path.
 * The order of the items is from the outermost symbol root to the innermost, with the last path being
 * the nativeIdPath provided as input
 *
 * Input path: ['sym-1', 'sym-2', 'some-element']
 * Output paths:
 * [
 *  ['sym-1', 'root-of-sym-1']
 *  ['sym-1', 'sym-2', 'root-of-sym-2']
 *  ['sym-1', 'sym-2', 'some-element']
 * ]
 */
export const generatePathsToAllRootsFromMap = (
  nativeIdPath: NativeId[],
  symbolInstanceToRootMap: SymbolInstanceToRootIdMap
): NativeId[][] => {
  const pathSegmentsStack = initial(nativeIdPath);
  const res: NativeId[][] = [];

  while (pathSegmentsStack.length) {
    const instanceId = getGuidNullable(last(pathSegmentsStack));
    const instanceSuffix = getInstanceSuffix(last(pathSegmentsStack));
    // @ts-expect-error - TS2322 - Type 'string | null | undefined' is not assignable to type 'string | null'.
    const rootId: NativeId | null = instanceId
      ? symbolInstanceToRootMap[instanceId]
      : null;

    if (!rootId) {
      // If we didn't find a root id, return an empty array to avoid
      // returning corrupt / invalid paths
      return emptyArray;
    }

    const newNativeId = `${rootId}${instanceSuffix}`;

    // Ignore the new path if the root id we found
    // is the element `nativeIdPath` leads to, as this path
    // will be pushed before the function returns.
    if (newNativeId !== last(nativeIdPath)) {
      const newPath = toNativeIdPath([...pathSegmentsStack, newNativeId]);
      res.unshift(newPath);
    }

    pathSegmentsStack.pop();
  }

  res.push(nativeIdPath);
  return res;
};

export const getPathsToAllInstanceRoots = (
  nativeIdPath: NativeId[],
  componentMap: Component.ComponentMap
): NativeId[][] => {
  if (!nativeIdPath.length) {
    return emptyArray;
  }

  const instanceIdToRootIdMap = createSymbolInstanceIdToRootIdMap(
    nativeIdPath,
    componentMap
  );

  if (!instanceIdToRootIdMap) {
    return emptyArray;
  }

  return generatePathsToAllRootsFromMap(nativeIdPath, instanceIdToRootIdMap);
};

/**
 * Determines if a path leads directly to any symbol root, regardless of nesting level.
 */
export const isPathToSymbolRoot = (
  nativeIdPath: NativeId[],
  componentMap: Component.ComponentMap
): boolean => {
  const id = getGuidNullable(last(nativeIdPath));
  return id ? isUserComponentRootId(componentMap, id) : false;
};

// A slot-default selection derives its single selected id to the outermost
// instance. Each check below is a cheaper filter before a more expensive one,
// so common selections exit before the full path classification.
export const isSlotDefaultSelection = (
  selectedNativeIdPath: NativeId[],
  componentMap: Component.ComponentMap,
  selectedNodeNativeId: string,
  rootElement: Expression,
  plugins?: Plugins.PluginsType
): boolean => {
  if (!plugins || !selectedNativeIdPath || selectedNativeIdPath.length < 2)
    return false;

  const endsAtComponentRoot = isPathToSymbolRoot(
    selectedNativeIdPath,
    componentMap
  );
  if (!endsAtComponentRoot) {
    const derivedIdHasLiveElement = Boolean(
      Expressions.getElementById(rootElement, selectedNodeNativeId)
    );
    if (derivedIdHasLiveElement) return false;
  } else {
    const innermostInstanceId = getGuidNullable(
      selectedNativeIdPath[selectedNativeIdPath.length - 2]
    );
    const derivedIdIsInnermostInstance =
      innermostInstanceId === selectedNodeNativeId;
    if (derivedIdIsInnermostInstance) {
      const derivedIdHasLiveElement = Boolean(
        Expressions.getElementById(rootElement, selectedNodeNativeId)
      );
      if (derivedIdHasLiveElement) return false;
    }
  }

  return (
    resolveSlotDefaultPathContext(
      selectedNativeIdPath,
      componentMap,
      plugins
    ) !== null
  );
};

// Given a valid native id path and a focused symbol path, returns the relative path.
// For more details:
// https://webflow.atlassian.net/wiki/spaces/CAP/pages/37684024/Tech+Spec+On+Canvas+Editing#Absolute-vs-Relative-native-id-paths
export const getRelativePath = (
  nativeIdPath: NativeId[],
  focusedSymbolNativeIdPath: NativeId[]
): NativeId[] => {
  const pathHeadItems = take(nativeIdPath, focusedSymbolNativeIdPath.length);
  if (nativeIdPathsEqual(pathHeadItems, focusedSymbolNativeIdPath)) {
    return drop(nativeIdPath, focusedSymbolNativeIdPath.length);
  } else {
    return [];
  }
};

// Given an absolute native id path and a focused symbol path, determines
// if the path leads to an element within the currently focused instance.
export const isPathTargetWithinFocusedSymbol = (
  nativeIdPath: NativeId[],
  focusedSymbolNativeIdPath: NativeId[]
): boolean => {
  return Boolean(
    getRelativePath(nativeIdPath, focusedSymbolNativeIdPath).length
  );
};

// Given a valid native id path, returns true if the path goes through symbol instances.
// Technically, this means that the path has a length of at least 2 since the last item
// in a native id path will always point to an element.
export const isPathContainingInstances = (
  nativeIdPath: NativeId[]
): boolean => {
  return nativeIdPath.length > 1;
};

// Given a valid native id path, returns true if the path goes through nested symbol instances.
// Technically, this means that the path has a length of at least 3 since the last item
// in a native id path will always point to an element.
export const isPathContainingNestedInstances = (
  nativeIdPath: NativeId[]
): boolean => {
  return nativeIdPath.length > 2;
};

// Returns true if the path leads to an element BELOW the root element. Otherwise, returns false.
// If a `focusedSymbolNativeIdPath` value is provided, it is assumed the `nativeIdPath` extends
// the `focusedSymbolNativeIdPath` and the check will be performed relative to it.
export const isPathToInstanceDescendant = (
  nativeIdPath: NativeId[],
  componentMap: Component.ComponentMap,
  focusedSymbolNativeIdPath?: NativeId[]
): boolean => {
  const relativePath = focusedSymbolNativeIdPath
    ? getRelativePath(nativeIdPath, focusedSymbolNativeIdPath)
    : nativeIdPath;

  const pathLength = relativePath.length;

  if (pathLength < 2) return false;
  if (pathLength === 2) {
    const nodeId = getGuidNullable(relativePath[relativePath.length - 1]);
    return nodeId !== null && !isUserComponentRootId(componentMap, nodeId);
  }
  return true;
};

/**
 * Returns true if the path leads to a top level symbol instance, relative to the currently focused component.
 * A top level symbol instance is a selection of a symbol instance that isn't nested within another component.
 */
export const isPathToTopLevelSymbolInstance = (
  nativeIdPath: NativeId[],
  componentMap: Component.ComponentMap,
  focusedSymbolNativeIdPath: NativeId[]
): boolean => {
  const relativePath = getRelativePath(nativeIdPath, focusedSymbolNativeIdPath);

  if (relativePath.length !== 2) return false;

  const maybeSymRootId = getGuidNullable(relativePath[relativePath.length - 1]);
  return maybeSymRootId
    ? isUserComponentRootId(componentMap, maybeSymRootId)
    : false;
};

const _memGetTargetElementOrInstance = weakMemo((nativeIdPath: NativeId[]) =>
  weakMemo((componentMap: Component.ComponentMap) => {
    const isPathToRoot = isPathToSymbolRoot(nativeIdPath, componentMap);

    if (isPathToRoot) {
      return getInnermostSymbolInstance(nativeIdPath, componentMap);
    } else {
      return getElementFromNodeInInstanceNativeId(nativeIdPath, componentMap);
    }
  })
);

/**
 * Given a path, returns the element as follows:
 * If the path leads to an instance root, this will be the symbol instance.
 * Otherwise, this is the element matching the path. We do this for display purposes when users interact with symbols on the canvas.
 */
export const getTargetElementOrInstance = (
  nativeIdPath: NativeId[],
  componentMap: Component.ComponentMap,
  plugins?: Plugins.PluginsType
): ExpressionElement | null => {
  if (!plugins) {
    return _memGetTargetElementOrInstance(nativeIdPath)(componentMap);
  }

  if (isPathToSymbolRoot(nativeIdPath, componentMap)) {
    return getInnermostSymbolInstance(nativeIdPath, componentMap, plugins);
  }

  // A live leaf hit can only be right or null, so the memoized result is safe
  // to reuse. Only a miss needs the defaults-aware lookup.
  return (
    _memGetTargetElementOrInstance(nativeIdPath)(componentMap) ??
    getElementFromNodeInInstanceNativeId(nativeIdPath, componentMap, plugins)
  );
};

/**
 * Finds the page-level Rich Text that owns a given element's own content (its
 * text, embeds, or wrappers, as opposed to a component placed inside it — see
 * `isRichTextDescendant`). Only meaningful for elements directly on the page;
 * a Rich Text inside a component instance is out of scope here.
 * @param plugins - passed to element resolution so it matches the caller's lookup
 * @returns the native id path to the enclosing Rich Text, or null if there isn't one
 */
export const getPathToEnclosingRichText = (
  nativeIdPath: NativeId[],
  componentMap: Component.ComponentMap,
  plugins?: Plugins.PluginsType
): NativeId[] | null => {
  const pageRender = getPageComponentRender(componentMap);
  const element = getTargetElementOrInstance(
    nativeIdPath,
    componentMap,
    plugins
  );

  if (!pageRender || !element) {
    return null;
  }

  const enclosingRichText = findFurthestAncestor(
    pageRender,
    element,
    isRichText
  );

  return enclosingRichText ? toNativeIdPath([enclosingRichText.id]) : null;
};

const findInnermostSymbolInstance = (
  nativeIdPath: NativeId[],
  componentMap: Component.ComponentMap,
  plugins?: Plugins.PluginsType
): ExpressionElement | null => {
  const root = getPageComponentRootElement(componentMap);
  if (!root) return null;

  let symInstanceElement = null;
  const visitor = (symInstance: ExpressionElement) => {
    symInstanceElement = symInstance;
  };

  visitNativeIdPathSymbolInstances(
    nativeIdPath,
    root,
    componentMap,
    visitor,
    plugins
  );
  return symInstanceElement;
};

const _memGetInnermostSymbolInstance = weakMemo((nativeIdPath: NativeId[]) =>
  weakMemo((componentMap: Component.ComponentMap) =>
    findInnermostSymbolInstance(nativeIdPath, componentMap)
  )
);

// Slot resolution asks this of the same path from several callers per drag frame.
const _memGetInnermostSymbolInstanceWithPlugins = weakMemo(
  (nativeIdPath: NativeId[]) =>
    weakMemo((componentMap: Component.ComponentMap) =>
      weakMemo((plugins: Plugins.PluginsType) =>
        findInnermostSymbolInstance(nativeIdPath, componentMap, plugins)
      )
    )
);

/**
 * Returns the inner most symbol instance from a NativeId when a child element is selected.
 * i.e this path: ['inst1', 'inst-2', 'heading'] will return the 'inst-2' element as an ExpressionElement
 */
export const getInnermostSymbolInstance = (
  nativeIdPath: NativeId[],
  componentMap: Component.ComponentMap,
  plugins?: Plugins.PluginsType
): ExpressionElement | null => {
  if (!plugins) {
    return _memGetInnermostSymbolInstance(nativeIdPath)(componentMap);
  }

  return _memGetInnermostSymbolInstanceWithPlugins(nativeIdPath)(componentMap)(
    plugins
  );
};

/**
 * Returns all component instances along a native id path, ordered outermost-first.
 * e.g. for path ['inst-1', 'inst-2', 'heading'], returns [inst-1-element, inst-2-element].
 */
export const getAllComponentInstances = (
  nativeIdPath: NativeId[],
  componentMap: Component.ComponentMap
): ExpressionElement[] => {
  const root = getPageComponentRootElement(componentMap);
  if (!root) return [];

  const instances: ExpressionElement[] = [];
  visitNativeIdPathSymbolInstances(
    nativeIdPath,
    root,
    componentMap,
    (symInstance: ExpressionElement) => {
      instances.push(symInstance);
    }
  );
  return instances;
};

/**
 * Returns the outer most symbol instance from a native id path.
 * If focusedSymbolNativeIdPath, the function will return the outermost symbol instance of the _relative_ native id path,
 * relative to the focused path.
 */
export const getOutermostSymbolInstance = (
  nativeIdPath: NativeId[],
  componentMap: Component.ComponentMap,
  focusedSymbolNativeIdPath?: NativeId[],
  plugins?: Plugins.PluginsType
): ExpressionElement | null => {
  const root = getPageComponentRootElement(componentMap);
  if (!root) return null;

  const relativePath = focusedSymbolNativeIdPath
    ? getRelativePath(nativeIdPath, focusedSymbolNativeIdPath)
    : nativeIdPath;
  const outermostInstanceId = getGuidNullable(first(relativePath));

  let resultSymInst: ExpressionElement | null = null;
  visitNativeIdPathSymbolInstances(
    nativeIdPath,
    root,
    componentMap,
    (symInst) => {
      if (symInst.id === outermostInstanceId) {
        resultSymInst = symInst;
      }
    },
    plugins
  );

  return resultSymInst;
};

/**
 * Given a `nativeIdPath`, returns the native id path of the outermost symbol,
 * taking into account the currently focused symbol
 */
export const getPathToOutermostSymbolRoot = (
  nativeIdPath: NativeId[],
  componentMap: Component.ComponentMap,
  focusedSymbolNativeIdPath: NativeId[] = []
): NativeId[] | null => {
  const outermostSymInstance = getOutermostSymbolInstance(
    nativeIdPath,
    componentMap,
    focusedSymbolNativeIdPath
  );
  const symRoot = outermostSymInstance
    ? getComponentInstRootElement(componentMap)(outermostSymInstance)
    : null;

  if (!symRoot || !outermostSymInstance) return null;

  const relativePath = getRelativePath(nativeIdPath, focusedSymbolNativeIdPath);
  const outermostSymInstanceNativeId = relativePath[0] || null;
  if (!outermostSymInstanceNativeId) return null;

  const maybeNativeIdSuffix = getInstanceSuffix(outermostSymInstanceNativeId);

  const nextSymNativeId = `${outermostSymInstance.id}${maybeNativeIdSuffix}`;
  const nextRootNativeId = `${symRoot.id}${maybeNativeIdSuffix}`;

  return toNativeIdPath([
    ...focusedSymbolNativeIdPath,
    nextSymNativeId,
    nextRootNativeId,
  ]);
};

/**
 * Given a native id path of length greater than 1, returns the component ID of
 * the outermost component instance. If provided with a native id path of length
 * less than or equal to 1, returns null.
 */
export const getOutermostUserComponentId = (
  nativeIdPath: NativeId[],
  componentMap: Component.ComponentMap
): null | Component.ComponentName => {
  if (nativeIdPath.length < 1) return null;

  const root = getPageComponentRender(componentMap);
  if (!root) return null;

  const outermostInstanceId = getGuidNullable(first(nativeIdPath));
  if (!outermostInstanceId) return null;

  // Get the outermost instance (in a nested component scenario), that it will be always present at page level
  const instanceElement = Expressions.getElementById(root, outermostInstanceId);
  if (!instanceElement) return null;

  // Get the component of the outermost instance
  return isUserComponentInst(componentMap, instanceElement)
    ? getName(instanceElement.type)
    : null;
};

export const getInnermostInstanceRootElement = (
  nativeIdPath: NativeId[],
  componentMap: Component.ComponentMap,
  plugins?: Plugins.PluginsType
): ExpressionElement | null => {
  const innermostSymbol = getInnermostSymbolInstance(
    nativeIdPath,
    componentMap,
    plugins
  );
  const maybeRoot = innermostSymbol
    ? getComponentInstRootElement(componentMap)(innermostSymbol)
    : null;
  return maybeRoot || null;
};

/**
 * Given a valid native id path and a focused symbol path, returns the outermost component instance id.
 */
export const getOutermostInstanceId = (
  nativeIdPath: NativeId[],
  focusedSymbolNativeIdPath: NativeId[]
): string | null | undefined => {
  const relativePath = getRelativePath(nativeIdPath, focusedSymbolNativeIdPath);
  return getGuidNullable(first(relativePath));
};

/**
 * Determine if two native id paths are equal
 */
export const nativeIdPathsEqual = (
  nativeIdPathA: NativeId[],
  nativeIdPathB: NativeId[]
): boolean => {
  if (nativeIdPathA.length !== nativeIdPathB.length) return false;

  for (let i = 0; i < nativeIdPathA.length; i++) {
    if (nativeIdPathA[i] !== nativeIdPathB[i]) return false;
  }

  return true;
};

/**
 * Given a native id path to a node in instance, returns the target element of the path.
 * If the native id path leads to a root of a symbol instance, it will return the root element.
 */
export const getElementFromNodeInInstanceNativeId = (
  nativeIdPath: NativeId[],
  componentMap: Component.ComponentMap,
  plugins?: Plugins.PluginsType
): ExpressionElement | null => {
  if (isPathContainingInstances(nativeIdPath)) {
    const innermostRoot = getInnermostInstanceRootElement(
      nativeIdPath,
      componentMap,
      plugins
    );
    const targetId = getGuidNullable(last(nativeIdPath));

    if (!innermostRoot || !targetId) return null;

    if (Expressions.getElementId(innermostRoot) === targetId) {
      return innermostRoot;
    }

    const found = Expressions.getElementById(EElement(innermostRoot), targetId);
    if (found) return found;

    if (!plugins) return null;
    const owner = getInnermostSymbolInstance(
      nativeIdPath,
      componentMap,
      plugins
    );
    if (!owner) return null;

    const slotDefaultElement = findSlotDefaultElement(
      plugins,
      componentMap,
      owner.type,
      targetId
    )?.element;

    return slotDefaultElement ?? null;
  } else {
    const root = getPageComponentRootElement(componentMap);
    const id = getGuidNullable(first(nativeIdPath));
    return root && id ? Expressions.getElementById(EElement(root), id) : null;
  }
};

export const getTargetElementId = (
  nativeIdPath: NativeId[]
): string | null | undefined => getGuidNullable(last(nativeIdPath));

export const getPathsToSymbolRoots = (
  nativeIdPath: NativeId[],
  focusedSymbolNativeIdPath: NativeId[],
  componentMap: Component.ComponentMap
): NativeId[][] => {
  const allRoots = getPathsToAllInstanceRoots(nativeIdPath, componentMap);
  const targetPath = last(allRoots);
  // If the target element is not a component root, then we remove it
  const roots =
    targetPath && isPathToSymbolRoot(targetPath, componentMap)
      ? allRoots
      : initial(allRoots);

  return drop(roots, focusedSymbolNativeIdPath.length);
};

export const isEditingSelectedNodeInInstance = (
  nativeIdPath: NativeId[],
  focusedSymbolNativeIdPath: NativeId[]
): boolean =>
  Boolean(
    nativeIdPath &&
    nativeIdPath.length > 1 &&
    (!focusedSymbolNativeIdPath.length ||
      nativeIdPath.length - focusedSymbolNativeIdPath.length > 1)
  );

type DefaultValueResolverFn = (
  sym: ExpressionElement,
  field: ExpressionPath
) => Expression;

/**
 * Given a `NativeId` path to an element and a data `ExpressionPath`, returns
 * the Expression bound to the value at the provided path. This function takes into
 * account there could be a chain of field overrides through which the expression
 * is provided to the target element.
 *
 * Optionally, a `resolveDefaultValExpr` callback can be provided by the caller as
 * a means to resolve the default value during the iteration.
 */
export const getBoundExpressionAtPath = (
  nativeIdPath: NativeId[],
  fieldPath: ExpressionPath,
  componentMap: Component.ComponentMap,
  root: ExpressionElement,
  resolveDefaultValExpr?: DefaultValueResolverFn
): Expression | null => {
  if (nativeIdPath.length === 1) {
    // When the length of the native id path is 1, it is expected
    // that the id represents a non-symbol target.
    const elem = safelyGetFirstElementInPath(nativeIdPath, root);
    const elemData = elem ? Expressions.getElementData(elem) : null;
    const exprAtPath = elemData ? Expressions.getIn(elemData, fieldPath) : null;
    return exprAtPath || null;
  }

  const sym = safelyGetFirstElementInPath(nativeIdPath, root);
  const symRoot = sym ? getComponentInstRootElement(componentMap)(sym) : null;

  if (!symRoot || !sym) return null;

  const nextPath = drop(nativeIdPath, 1);
  const selectExprOrOtherExpr = getBoundExpressionAtPath(
    nextPath,
    fieldPath,
    componentMap,
    symRoot,
    resolveDefaultValExpr
  );

  if (!selectExprOrOtherExpr) return null;

  return isSelectExpr(selectExprOrOtherExpr)
    ? selectFromSymInstanceData(selectExprOrOtherExpr)(
        sym,
        resolveDefaultValExpr
      )
    : selectExprOrOtherExpr;
};

/**
 * Given a native id path, returns the first element. This can be a "regular" element
 * or a symbol instance.
 */
const safelyGetFirstElementInPath = (
  nativeIdPath: NativeId[],
  root: ExpressionElement
) => {
  const guid = getGuidNullable(nativeIdPath[0]);
  return guid ? Expressions.getElementById(EElement(root), guid) : null;
};

const selectFromSymInstanceData = matchExpression({
  Select:
    ({from, prop}) =>
    (
      sym: ExpressionElement,
      resolveDefaultValExpr?: DefaultValueResolverFn
    ) => {
      if (Expressions.isDataVariable(from as Expression)) {
        const symData = Expressions.getElementData(sym);
        const fieldVal = Expressions.getIn(symData, [{in: 'Record', at: prop}]);
        if (fieldVal) return fieldVal;

        return resolveDefaultValExpr
          ? resolveDefaultValExpr(sym, [{in: 'Record', at: prop}])
          : null;
      }

      return null;
    },
  _: () => (_0: ExpressionElement, _1: undefined | DefaultValueResolverFn) =>
    null,
});

const isSelectExpr = matchExpression({
  Select: constantTrue,
  _: constantFalse,
});

/**
 * Given an element's nativeIdPath and the component map, returns the element's
 * parent's nativeIdPath.
 */
export const getParentNativeIdPath = (
  nativeIdPath: NativeId[],
  componentMap: Component.ComponentMap,
  focusedSymbolNativeIdPath: NativeId[]
): NativeId[] | null => {
  const root =
    focusedSymbolNativeIdPath.length > 0
      ? getInnermostInstanceRootElement(nativeIdPath, componentMap)
      : getPageComponentRootElement(componentMap);
  if (!root) return null;

  // The last element in the path is the native id of the target element to wrap
  const targetNativeId = nativeIdPath[nativeIdPath.length - 1];
  const targetGuid = getGuidNullable(targetNativeId);

  if (!targetGuid) return null;

  // Find the parent the root. This is safe, because we never want to wrap outside the boundaries of symbols
  const parent = Expressions.getParent(EElement(root), targetGuid);

  if (!parent) return null;

  // Account for `_instance-X` suffix that can exist on elements inside collection lists
  // we need to attach those back
  const maybeNativeIdSuffix = getInstanceSuffix(targetNativeId);
  const parentNativeId = `${parent.id}${maybeNativeIdSuffix}`;

  // Return a new path based on the n-1 ids in the original path
  return toNativeIdPath([...initial(nativeIdPath), parentNativeId]);
};

/**
 * Given a `nativeIdPath` of an element and a `focusedSymbolNativeIdPath` of the currently focused component,
 * derives a single `NativeId` that matches the element targeted by `nativeIdPath`.
 *
 * A `nativeIdPath` uniquely identifies an element on the Canvas, but in some scenarios we need to identify the single
 * `NativeId` of the element the user is interacting with. To derive what that `NativeId` is, we must take into account
 * the component the user is currently interacting with (i.e the focused component), and whether the path leads to a selection
 * within other user components (sometimes referred to as "deep selection").
 *
 * The following logic applies:
 * 1. If the path leads to an element inside a user component, the single native id matching will be that of the outermost component instance.
 * 2. If the path leads to an element that isn't inside of a component, the first (and only) id is returned
 * This is obtained by getting the first item in the *relative* native id path, because the outermost component can be a component nested within the one
 * the user is currently working on.
 *
 * This function is used, for example, when NODE_CLICKED actions are dispatched with `nativeIdPath` and we need to consistently
 * set the correct *single* native id to the state which other systems rely on.
 *
 */
export const deriveNativeIdFromPath = (
  nativeIdPath: NativeId[],
  focusedSymbolNativeIdPath: NativeId[]
): NativeId | null => {
  if (isPathContainingInstances(nativeIdPath)) {
    // If a path contains instances, we return the first element of the relative path
    const relativePath = getRelativePath(
      nativeIdPath,
      focusedSymbolNativeIdPath
    );

    return relativePath[0] || null;
  } else if (nativeIdPath.length === 1) {
    // Otherwise, the native id path does not go through any component instances, and we return the only element we expect in the array
    return nativeIdPath[0] || null;
  } else {
    return null;
  }
};

/**
 * Given the `nativeId` of an element, `focusedSymbolNativeIdPath` of the currently focused component, and Component map,
 * derives the full `NativeId` path to the element matching the provided `nativeId`.
 *
 * This function is used when the full `nativeIdPath` to an element on the Canvas is needed, e.g. when determining the
 * next element that should be selected on Canvas after a `DELETE_KEY_PRESSED` action was dispatched on a sibling element.
 */
export const derivePathFromNativeId = (
  nativeId: NativeId,
  focusedSymbolNativeIdPath: NativeId[],
  componentMap: Component.ComponentMap
): NativeId[] | null => {
  /**
   * Since the element with `nativeId` may be contained within a Component (and possibly deeply nested),
   * the current Component's Expression is checked for an element with matching GUID, derived from the `nativeId`.
   */
  const currentComponentRoot = getCurrentComponent(
    focusedSymbolNativeIdPath,
    componentMap
  );
  const elementId = getGuidNullable(nativeId);
  const maybeExistingElement =
    currentComponentRoot && elementId
      ? Expressions.getElementById(EElement(currentComponentRoot), elementId)
      : null;

  if (
    maybeExistingElement &&
    isUserComponentInst(componentMap, maybeExistingElement)
  ) {
    /**
     * When a matching element is found, and it's a User Component instance, a `nativeIdPath`
     * is created which includes the instance's `NativeId` and Component root's `NativeId` (with possibly suffix appended).
     */
    const componentInst = maybeExistingElement;
    const componentRoot =
      getComponentInstRootElement(componentMap)(componentInst);

    if (!componentRoot) return null;

    const maybeSuffix = getInstanceSuffix(nativeId) || '';
    const componentInstanceNativeId = `${componentInst.id}${maybeSuffix}`;
    const componentRootNativeId = `${componentRoot.id}${maybeSuffix}`;

    return toNativeIdPath([
      ...focusedSymbolNativeIdPath,
      componentInstanceNativeId,
      componentRootNativeId,
    ]);
  }

  /**
   * A Component instance can also be authored in the current Component's slot defaults,
   * which live on its `defaultData` rather than its render, so it is not found above.
   */
  if (!maybeExistingElement && elementId) {
    const resolveMissedSegment = mkMissedFocusSegmentResolver(componentMap);
    const defaultInstanceType = resolveMissedSegment(
      getCurrentComponentType(
        componentMap,
        focusedSymbolNativeIdPath,
        resolveMissedSegment
      ),
      elementId
    );
    const componentRoot = defaultInstanceType
      ? getComponentRootElement(componentMap, defaultInstanceType)
      : null;

    if (componentRoot) {
      const maybeSuffix = getInstanceSuffix(nativeId) || '';

      return toNativeIdPath([
        ...focusedSymbolNativeIdPath,
        `${elementId}${maybeSuffix}`,
        `${componentRoot.id}${maybeSuffix}`,
      ]);
    }
  }

  /**
   * When the element for `nativeId` is not found within a Component, sets the `nativeIdPath` as the
   * provided `nativeId` appended to the `focusedSymbolNativeIdPath`.
   */
  return toNativeIdPath([...focusedSymbolNativeIdPath, nativeId]);
};

/**
 * Given an element and a native id path to anchor it against, returns the full
 * native id path to that element, or `null` when it is not a Slot default of
 * any Component instance along that anchor.
 *
 * Each Component instance on the anchor is asked in turn, from the innermost
 * outward, so the anchor can point anywhere inside the owning instance.
 *
 * Use it to turn an element id into a path when a selection or hover path is
 * already in hand, such as the Slot row in the component properties panel.
 */
export const getNativeIdPathToSlotDefault = (
  element: ExpressionElement | null,
  componentMap: Component.ComponentMap,
  anchorNativeIdPath: NativeId[],
  plugins: Plugins.PluginsType
): NativeId[] | null => {
  if (!element) return null;

  for (let i = anchorNativeIdPath.length; i > 0; i--) {
    const instanceNativeIdPath = anchorNativeIdPath.slice(0, i);
    const instanceNativeId = instanceNativeIdPath[i - 1];
    if (!instanceNativeId) continue;

    const instance = getElementFromNodeInInstanceNativeId(
      instanceNativeIdPath,
      componentMap,
      plugins
    );

    // A segment that is not a Component instance carries no defaults of its own.
    if (!instance || !componentMap.get(instance.type)) continue;

    const isSlotDefault = Boolean(
      findSlotDefaultElement(plugins, componentMap, instance.type, element.id)
    );

    if (!isSlotDefault) continue;

    return getNativeIdPathToElement(
      element,
      componentMap,
      instanceNativeIdPath,
      instanceNativeId
    );
  }

  return null;
};

/**
 * Generates a native id for a projected element, based on some `projectingParentNativeId`
 * which is used to assign the correct index suffixes to the newly generated native id.
 */
export const generateNativeIdForProjectedElement = (
  element: ExpressionElement,
  projectingParentNativeId: NativeId | null
): NativeId => {
  const nativeId: NativeId = convertElementIdToNativeId(element.id);
  const instanceIndices = projectingParentNativeId
    ? getInstanceIndicesFromNativeId(projectingParentNativeId)
    : [];

  // The projected element can itself be another projecting element.
  if (isProjectingElement(element)) {
    return generateInstanceNativeId(nativeId, [...instanceIndices, 0]);
  } else if (instanceIndices.length) {
    return generateInstanceNativeId(nativeId, instanceIndices);
  } else {
    return nativeId;
  }
};

/**
 * Generates a `nativeIdPath` to an element, which is then used for every
 * node in the navigator, taking into account the `projectingParentNativeId`.
 */
export const getNativeIdPathToElement = (
  element: ExpressionElement,
  componentMap: Component.ComponentMap,
  focusedSymbolNativeIdPath: NativeId[],
  projectingParentNativeId: NativeId
): NativeId[] => {
  if (isUserComponentInst(componentMap, element)) {
    const root = getComponentInstRootElement(componentMap)(element) || null;
    if (!root) {
      throw new Error(
        `NavigatorUtils::getNativeIdPathToElement Could not find root of symbol instance ${element.id}`
      );
    }

    const symNativeId = generateNativeIdForProjectedElement(
      element,
      projectingParentNativeId
    );
    const rootNativeId = generateNativeIdForProjectedElement(
      root,
      projectingParentNativeId
    );

    return [...focusedSymbolNativeIdPath, symNativeId, rootNativeId];
  } else {
    const elementId = generateNativeIdForProjectedElement(
      element,
      projectingParentNativeId
    );

    return [...focusedSymbolNativeIdPath, elementId];
  }
};

/**
 * Resolves a focus-path segment that no render holds, by looking it up in the
 * owning component's slot defaults. Only a Component instance can be authored as
 * slot default content, and defaults live on `defaultData` as expressions at
 * rest, so this needs no plugins.
 */
export const mkMissedFocusSegmentResolver = weakMemo(
  (componentMap: Component.ComponentMap): MissedFocusSegmentResolver =>
    (ownerComponentType, elementId) => {
      const ownerComponent = componentMap.get(ownerComponentType);
      if (!ownerComponent) return null;

      return (
        getSlotDefaultComponentInstances(ownerComponent).find(
          ([defaultInstanceId]) => defaultInstanceId === elementId
        )?.[1] ?? null
      );
    }
);

/**
 * Given a `focusedSymbolNativeIdPath`, returns the native id path to the current focused component root.
 * If an empty `focusedSymbolNativeIdPath` is provided, or the current component is the page component, the function returns `null`
 */
export const getPathToFocusedComponentRoot = (
  focusedSymbolNativeIdPath: NativeId[],
  componentMap: Component.ComponentMap
): NativeId[] | null => {
  if (!focusedSymbolNativeIdPath.length) return null;

  const currentComponentType = getCurrentComponentType(
    componentMap,
    focusedSymbolNativeIdPath,
    mkMissedFocusSegmentResolver(componentMap)
  );

  if (namesEqual(pageComponentType, currentComponentType)) return null;

  const currentComponent = componentMap.get(currentComponentType);
  const currentComponentRootExpr =
    currentComponent && Component.getRender(currentComponent);
  const currentComponentRoot =
    currentComponentRootExpr &&
    Expressions.getElement(currentComponentRootExpr);

  if (!currentComponentRoot) return null;

  // Derive the instance suffix from the focused symbol native id path
  const maybeNativeIdSuffix = getInstanceSuffix(
    last(focusedSymbolNativeIdPath)
  );
  const rootNativeId = `${currentComponentRoot.id}${maybeNativeIdSuffix}`;

  return [...focusedSymbolNativeIdPath, rootNativeId];
};

/**
 * True when a path targets something *below* the root of a component placed as slot-default
 * content. The component root itself is excluded: there the component is the target, so the plain
 * outline already describes it and a node-in-instance pair would draw over it.
 */
export const isPathInsideNestedSlotDefaultComponent = (
  slotDefaultComponentBoundary: SlotDefaultComponentBoundary,
  focusedSymbolNativeIdPath: NativeId[],
  componentMap: Component.ComponentMap,
  nativeIdPath: NativeId[]
): boolean => {
  const focusedComponentRootPath = getPathToFocusedComponentRoot(
    focusedSymbolNativeIdPath,
    componentMap
  );
  if (!focusedComponentRootPath) return false;

  if (
    nativeIdPathsEqual(
      slotDefaultComponentBoundary.componentRootPath,
      focusedComponentRootPath
    )
  )
    return false;

  if (
    nativeIdPathsEqual(
      slotDefaultComponentBoundary.componentRootPath,
      nativeIdPath
    )
  )
    return false;

  // Both call sites resolve the boundary from the path they pass here, but this is
  // exported, so a caller pairing an unrelated boundary with a path would otherwise
  // get `true`. `componentRootPath` ends with the component's root, so dropping it
  // leaves the instance the path has to descend from.
  return Boolean(
    getRelativePath(
      nativeIdPath,
      slotDefaultComponentBoundary.componentRootPath.slice(0, -1)
    ).length
  );
};
